import { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Upload, FileText, X, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { crmPortalService } from '@/services/api/crm-portal';
import { cn } from '@/lib/utils';
import type { IntakeDocumentType } from '@/types/intake';

interface UploadDocumentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  documentType: IntakeDocumentType | null;
  existingDocumentId?: string;
  isReupload?: boolean;
}

export function UploadDocumentDialog({
  open,
  onOpenChange,
  documentType,
  existingDocumentId,
  isReupload = false,
}: UploadDocumentDialogProps) {
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [description, setDescription] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadState, setUploadState] = useState<'idle' | 'uploading' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const resetState = () => {
    setFile(null);
    setDescription('');
    setUploadProgress(0);
    setUploadState('idle');
    setErrorMessage('');
  };

  const handleClose = () => {
    if (uploadState !== 'uploading') {
      resetState();
      onOpenChange(false);
    }
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      const selectedFile = acceptedFiles[0];

      // Check file size
      const maxSizeMb = documentType?.maxFileSizeMb || 10;
      if (selectedFile.size > maxSizeMb * 1024 * 1024) {
        setErrorMessage(`File size exceeds ${maxSizeMb}MB limit`);
        return;
      }

      // Check mime type
      const acceptedTypes = documentType?.acceptedMimeTypes || ['application/pdf', 'image/jpeg', 'image/png'];
      if (!acceptedTypes.includes(selectedFile.type)) {
        setErrorMessage(`File type not accepted. Allowed: ${acceptedTypes.join(', ')}`);
        return;
      }

      setFile(selectedFile);
      setErrorMessage('');
    }
  }, [documentType]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    accept: documentType?.acceptedMimeTypes?.reduce((acc, type) => {
      acc[type] = [];
      return acc;
    }, {} as Record<string, string[]>) || {
      'application/pdf': [],
      'image/jpeg': [],
      'image/png': [],
    },
  });

  const uploadMutation = useMutation({
    mutationFn: async () => {
      if (!file || !documentType) throw new Error('No file or document type');

      setUploadState('uploading');
      setUploadProgress(10);

      // Get presigned URL
      const { uploadUrl, documentId, s3Key } = await crmPortalService.getDocumentUploadUrl(
        documentType.id,
        file.name,
        file.type
      );
      setUploadProgress(30);

      // Upload to S3
      await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });
      setUploadProgress(70);

      // Confirm upload
      await crmPortalService.confirmDocumentUpload(documentId);
      setUploadProgress(100);

      return { documentId };
    },
    onSuccess: () => {
      setUploadState('success');
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['crm-documents'] });
      queryClient.invalidateQueries({ queryKey: ['crm-progress'] });

      // Close dialog after a short delay to show success state
      setTimeout(() => {
        handleClose();
      }, 1500);
    },
    onError: (error: Error) => {
      setUploadState('error');
      setErrorMessage(error.message || 'Upload failed. Please try again.');
    },
  });

  const handleUpload = () => {
    if (file && documentType) {
      uploadMutation.mutate();
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {isReupload ? 'Re-upload Document' : 'Upload Document'}
          </DialogTitle>
          <DialogDescription>
            {documentType?.name}
            {documentType?.description && (
              <span className="block mt-1 text-muted-foreground">
                {documentType.description}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Success State */}
          {uploadState === 'success' && (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-500/10 mb-4">
                <CheckCircle2 className="h-8 w-8 text-green-500" />
              </div>
              <p className="font-medium text-lg">Upload Successful!</p>
              <p className="text-sm text-muted-foreground">
                Your document has been uploaded and is being processed.
              </p>
            </div>
          )}

          {/* Error State */}
          {uploadState === 'error' && (
            <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4">
              <div className="flex items-center gap-2 text-red-500">
                <AlertCircle className="h-5 w-5" />
                <span className="font-medium">Upload Failed</span>
              </div>
              <p className="mt-1 text-sm text-red-400">{errorMessage}</p>
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={() => {
                  setUploadState('idle');
                  setUploadProgress(0);
                }}
              >
                Try Again
              </Button>
            </div>
          )}

          {/* Upload Form */}
          {(uploadState === 'idle' || uploadState === 'uploading') && (
            <>
              {/* Dropzone */}
              <div
                {...getRootProps()}
                className={cn(
                  'border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors',
                  isDragActive && 'border-primary bg-primary/5',
                  !isDragActive && 'border-muted-foreground/25 hover:border-primary/50',
                  uploadState === 'uploading' && 'pointer-events-none opacity-50'
                )}
              >
                <input {...getInputProps()} disabled={uploadState === 'uploading'} />

                {file ? (
                  <div className="flex items-center justify-center gap-3">
                    <FileText className="h-10 w-10 text-primary" />
                    <div className="text-left">
                      <p className="font-medium truncate max-w-[250px]">{file.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {formatFileSize(file.size)}
                      </p>
                    </div>
                    {uploadState === 'idle' && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="ml-auto"
                        onClick={(e) => {
                          e.stopPropagation();
                          setFile(null);
                        }}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ) : (
                  <>
                    <Upload className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                    <p className="font-medium">
                      {isDragActive ? 'Drop the file here' : 'Drag & drop a file here'}
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      or click to browse
                    </p>
                    <p className="text-xs text-muted-foreground mt-2">
                      Max size: {documentType?.maxFileSizeMb || 10}MB
                    </p>
                  </>
                )}
              </div>

              {/* Error message for validation */}
              {errorMessage && uploadState === 'idle' && (
                <p className="text-sm text-red-500">{errorMessage}</p>
              )}

              {/* Upload Progress */}
              {uploadState === 'uploading' && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span>Uploading...</span>
                    <span>{uploadProgress}%</span>
                  </div>
                  <Progress value={uploadProgress} className="h-2" />
                </div>
              )}

              {/* Optional Description */}
              <div className="space-y-2">
                <Label htmlFor="description">Description (optional)</Label>
                <Textarea
                  id="description"
                  placeholder="Add any notes about this document..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  disabled={uploadState === 'uploading'}
                  className="resize-none"
                  rows={3}
                />
              </div>
            </>
          )}
        </div>

        {uploadState !== 'success' && (
          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={uploadState === 'uploading'}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpload}
              disabled={!file || uploadState === 'uploading'}
            >
              {uploadState === 'uploading' ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-4 w-4" />
                  Upload
                </>
              )}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
